import { useEffect, useMemo, useState } from 'react';

interface TypewriterCycleProps {
  /**
   * List of strings to cycle through one at a time.
   */
  copy: string[];
  /**
   * Delay in ms before starting the next entry.
   */
  delay?: number;
  /**
   * Static text rendered before each cycled entry.
   */
  prefix?: string;
  /**
   * Shuffle string order, excluding the first entry.
   */
  random?: boolean;
  /**
   * Static text rendered after each cycled entry.
   */
  suffix?: string;
  /**
   * Delay in ms between each letter being printed.
   */
  timing?: number;
}

const TypewriterCycle = ({
  copy,
  delay = 1500,
  prefix = '',
  random = false,
  suffix = '',
  timing = 80,
}: TypewriterCycleProps) => {
  const [index, setIndex] = useState(0);

  const entries = useMemo(() => {
    if (!random || copy.length <= 2) return copy;
    const [first, ...rest] = copy;
    // oxlint-disable-next-line no-plusplus
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    return [first, ...rest];
  }, [copy, random]);

  const [text, setText] = useState(() => entries[0]?.slice(0, 1) ?? '');

  useEffect(() => {
    if (!entries.length) return undefined;

    const current = entries[index % entries.length];

    if (text.length < current.length) {
      const id = setTimeout(() => setText(current.slice(0, text.length + 1)), timing);
      return () => clearTimeout(id);
    }

    const id = setTimeout(() => {
      const next = entries[(index + 1) % entries.length];
      setText(next.slice(0, 1));
      setIndex(i => (i + 1) % entries.length);
    }, delay);

    return () => clearTimeout(id);
  }, [entries, delay, index, text, timing]);

  return (
    <>
      {prefix}
      {text}
      {suffix}
    </>
  );
};

export default TypewriterCycle;
